// ── Transaction fetch status (TSB-4301) ──────────────────────────────────────
//
// The dashboard fans out one request per tenant × component. Before this, any
// request that failed was silently dropped, and if every one failed the result
// was an empty array that overwrote good data on screen. A backend outage and a
// genuinely quiet period rendered identically — the defect in TSB-4301.
//
// These helpers turn a batch of settled promises into something the UI can be
// honest about: how many sources answered, whether the numbers on screen are
// complete, and a reference the user can quote to support.
//
// Error text shown to a user is deliberately generic (CWE-209): no status
// codes, hostnames, URLs or upstream messages. The full error goes to the
// console; only the reference id crosses to the screen.

export const TX_STATUS_IDLE = { state: 'idle', total: 0, failed: 0, loadedAt: null, reference: null, reason: null };

/**
 * Which kind of failure this is. Different causes deserve different sentences —
 * lumping them into "something went wrong" is what the guidance warns against.
 */
export function classifyFetchError(err) {
  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') return 'timeout';
  if (!err?.response) return 'offline';
  const status = err.response.status;
  if (status === 429) return 'throttled';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  return 'unknown';
}

/**
 * An id the user can quote to support. API Gateway sets x-amzn-RequestId on
 * every response; an RFC 9457 problem body may carry traceId instead. Both are
 * opaque, so both are safe to display.
 */
export function referenceFrom(err) {
  const headers = err?.response?.headers || {};
  return (
    headers['x-amzn-requestid'] ||
    headers['x-amzn-trace-id'] ||
    headers['x-request-id'] ||
    err?.response?.data?.traceId ||
    null
  );
}

const REASON_TEXT = {
  timeout:   "The service didn't respond in time. This is usually temporary.",
  offline:   "Couldn't reach the service. Check your connection and try again.",
  throttled: 'The service is busy right now. Please try again shortly.',
  server:    'The service reported a problem. This is usually temporary.',
  client:    "The request couldn't be completed.",
  unknown:   "The request couldn't be completed.",
};

export function reasonText(reason) {
  return REASON_TEXT[reason] || REASON_TEXT.unknown;
}

/**
 * Fold a settled batch into the next status.
 *
 * `previous` matters: when every request fails but we already had data on
 * screen, the honest state is "stale" — keep showing it, say when it is from —
 * rather than blanking to zeros.
 */
export function nextTxStatus(results, previous, nowLabel) {
  const rejected = results.filter(r => r.status === 'rejected');
  const total = results.length;

  if (total === 0) return previous;

  if (rejected.length === total) {
    const first = rejected[0].reason;
    return {
      state: previous.loadedAt ? 'stale' : 'failed',
      total,
      failed: rejected.length,
      loadedAt: previous.loadedAt,
      reference: referenceFrom(first),
      reason: classifyFetchError(first),
    };
  }

  if (rejected.length > 0) {
    const first = rejected[0].reason;
    return {
      state: 'partial',
      total,
      failed: rejected.length,
      loadedAt: nowLabel,
      reference: referenceFrom(first),
      reason: classifyFetchError(first),
    };
  }

  return { state: 'ok', total, failed: 0, loadedAt: nowLabel, reference: null, reason: null };
}

/** True when the batch produced nothing usable, so panels must not show zeros. */
export const isBlankFailure = status => status.state === 'failed';

/** True when figures on screen are computed from an incomplete set of sources. */
export const isDegraded = status => status.state === 'partial';
